/** @jsx jsx */
import { jsx } from 'theme-ui';
import PropTypes from 'prop-types';
import Link from '../link';
import UnbulletedList from '../styles/unbulleted-list';
import { colors } from '../../gatsby-plugin-theme-ui';

export const Container = ({ primaryNavOpen, ...props }) => (
  <div
    sx={{
      bg: [null, null, 'PrimaryDarkBlue'],
      display: [primaryNavOpen ? 'block' : 'none', null, 'flex'],
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: [null, null, '0 2px 4px 0 rgba(0, 0, 0, 0.25)'],
      position: ['absolute', null, 'static'],
      top: ['100%', null, 'auto'],
      zIndex: 50,
      px: ['1.4rem', '2.8rem', 0],
      width: '100%',
    }}
    {...props}
  />
);

Container.propTypes = {
  primaryNavOpen: PropTypes.bool.isRequired,
};

export const SiteTitle = (props) => (
  <Link
    sx={{
      bg: 'PrimaryDarkBlue',
      color: 'white',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontSize: ['2rem', null, '3rem'],
      lineHeight: 1,
      fontWeight: 'bold',
      py: ['1.4rem', null, 0],
      pl: ['1.4rem', '2.8rem', '6.4rem'],
      textDecoration: 'none',
      display: 'block',
    }}
    {...props}
  />
);

export const NavContainer = (props) => (
  <nav
    sx={{
      bg: ['white', null, 'transparent'],
      display: ['flex', null, 'block'],
      justifyContent: 'space-between',
      fontSize: '1.5rem',
      boxShadow: ['0 2px 4px 0 rgba(0, 0, 0, 0.5)', null, 'none'],
      position: 'relative',
    }}
    {...props}
  />
);

export const NavBuckets = (props) => (
  <div
    sx={{
      flexBasis: ['66.66666666%', '33.333333333%'],
      py: ['1.4rem', null, 0],
      display: [null, null, 'flex'],
    }}
    {...props}
  />
);

export const Bucket = ({ partiallyActive, ...props }) => (
  <UnbulletedList
    sx={{
      bg: partiallyActive ? ['ice', null, 'transparent'] : null,
      boxShadow: partiallyActive ? ['inset 0 1px 3px 0 #c2cace', 'none'] : null,
      position: [null, null, 'relative'],
      '&:hover, &.active:hover': {
        ul: {
          display: [null, null, 'block'],
        },
        '> li > a:after': {
          bg: 'BrightCyan',
        },
      },
      '&:last-child': {
        ul: {
          right: [null, null, 0],
          left: [null, null, 'auto'],
        },
      },
    }}
    {...props}
  />
);

Bucket.propTypes = {
  partiallyActive: PropTypes.bool.isRequired,
};

export const ChildBucket = (props) => (
  <UnbulletedList
    sx={{
      px: ['1.4rem', 0],
      pb: '2rem',
      pt: [null, '1.4rem'],
      position: [null, 'absolute'],
      top: [0, null, '100%'],
      left: ['33.333333333%', null, 0],
      zIndex: 50,
      width: [null, '33.333333333%', 'auto'],
      minWidth: [null, null, '26.8rem'],
      height: ['100%', null, 'auto'],
      bg: [null, 'ice', 'white'],
      boxShadow: [null, null, '0 2px 4px 0 rgba(0, 0, 0, 0.5)'],
    }}
    {...props}
  />
);

export const SinglePageBucket = (props) => (
  <UnbulletedList
    sx={{
      flexBasis: '33.333333333%',
      pt: '1.4rem',
      boxShadow: 'inset 0 1px 3px 0 rgba(0, 0, 0, 0.5)',
      display: [null, null, 'none'],
    }}
    {...props}
  />
);

export const SinglePageContainer = (props) => (
  <li
    sx={{
      flexBasis: '50%',
    }}
    {...props}
  />
);

export const NavLink = (props) => (
  <Link
    sx={{
      display: 'block',
      py: '1.2rem',
      px: ['1.4rem', '2.8rem'],
      color: 'PrimaryDarkBlue',
      fontWeight: 'bold',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: ['underline', null, 'none'],
        boxShadow: [null, null, `inset 0 1px 3px 0 ${colors.SecondaryGray3}`],
        bg: [null, null, 'ice'],
        color: [null, null, 'SecondaryBlue2'],
      },
      '&:active': {
        color: [null, null, 'PrimaryDarkBlue'],
      },
    }}
    activeClassName="active"
    partiallyActive
    {...props}
  />
);

export const SectionLink = (props) => (
  <NavLink
    sx={{
      fontSize: [null, null, '1.7rem'],
      py: [null, null, '3.65rem'],
      px: [null, null, '2.8rem'],
      color: [null, null, 'white'],
      position: 'relative',
      '&:after': {
        content: [null, null, '""'],
        display: 'block',
        position: 'absolute',
        left: 0,
        bottom: 0,
        bg: 'transparent',
        width: '100%',
        height: '1.6rem',
      },
      '+ ul': {
        display: 'none',
      },
      '&.active': {
        color: ['SecondaryBlue2', null, 'white'],
        bg: [null, null, 'SecondaryBlue2'],
        '&:after': {
          bg: 'SecondaryBlue2',
        },
        '&:hover': {
          bg: [null, null, 'SecondaryBlue2'],
        },
      },
      '&.active + ul': {
        display: ['block', null, 'none'],
        '&:hover': {
          display: 'block',
        },
      },
      '&:hover, &:active': {
        boxShadow: [null, null, 'none'],
        bg: [null, null, 'transparent'],
        color: [null, null, 'white'],
      },
    }}
    {...props}
  />
);
